import { Listbox } from '@headlessui/react';
import CommonButton from 'components/CommonButton/CommonButton';
import DefaultModal from 'components/Modals/DefaultModal';
import { ReactComponent as DropdownIcon } from 'assets/icons/chevron-down.svg';
import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import useModal from 'hooks/useModal';
import { renderToast } from 'components/Toast/Toast';
import axiosInstance from 'apis/axiosInstance';
import useCancelReason from 'hooks/useCancelReason';
import useMatchMutate from 'hooks/useMatchMutate';

const RejectAppointmentModal: FC<{
  appointmentId: string;
  onClose: () => void;
  onDone?: () => void;
}> = ({ appointmentId, onClose, onDone }) => {
  const [selectedCancelReason, setSelectedCancelReason] = useState({
    id: null,
    description: null,
  });

  const [otherReason, setOtherReason] = useState('');

  const { setIsAllowed } = useModal({ onClose });

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = useForm();

  const { data: cancelReasons = [] } = useCancelReason();

  const matchMutate = useMatchMutate();

  const onChangeReason = (value: any) => {
    setSelectedCancelReason(value);
  };

  const onSubmit = async () => {
    setIsAllowed(false);
    const payload = [
      {
        appointmentId,
        status: 'cancelled',
        cancelReasonCode: selectedCancelReason.id,
        customReason: otherReason || null,
      },
    ];
    try {
      await axiosInstance.patch('/appointments', payload);
      await matchMutate(/\/appointments\?[\s\S]+/);
      renderToast({
        type: 'success',
        message: `Successfully cancelled appointment`,
      });
      onClose();
    } catch (error) {
      renderToast({
        type: 'error',
        message: 'Something went wrong. Please try again later',
      });
    }
    setIsAllowed(true);
    if (onDone) {
      onDone();
    }
  };

  const renderCancelReasonDropdown = () => {
    return (
      <>
        <div className="mb-0.8">
          <span className="font-bold text-14">Select a reason</span>
          <span className="text-mid-grey">*</span>
        </div>
        <div>
          <Listbox value={selectedCancelReason} onChange={onChangeReason}>
            {({ open }) => {
              return (
                <div className="relative">
                  <Listbox.Button
                    className={`relative w-full h-[4.8rem] pl-1.6 cursor-pointer rounded-[1rem] bg-white border-[0.5px] border-light-grey text-left shadow-primary hover:border-magenta hover:shadow-input-active outline-none ${
                      open ? 'border-magenta shadow-input-active' : ''
                    }`}
                  >
                    <span
                      className={`block pr-3 ${
                        selectedCancelReason.id
                          ? 'text-darkest-grey'
                          : 'text-mid-grey'
                      } `}
                    >
                      {selectedCancelReason.id
                        ? selectedCancelReason.description
                        : 'Select reason'}
                    </span>
                    <span
                      className={`pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2`}
                    >
                      <DropdownIcon className="w-1.4 h-1.4" />
                    </span>
                  </Listbox.Button>
                  <Listbox.Options className="absolute mt-0.5 max-h-30 scrollbar w-full overflow-auto rounded-[0.8rem] bg-white py-1.5 text-base shadow-primary flex flex-col gap-y-1.5 z-10">
                    {cancelReasons.map((value) => (
                      <Listbox.Option
                        key={value.id}
                        className="relative cursor-default select-none pl-1.6"
                        value={value}
                      >
                        {({ selected }) => (
                          <span
                            className={`block truncate cursor-pointer hover:text-magenta ${
                              selected ? 'text-magenta' : ''
                            }`}
                          >
                            {value.description}
                          </span>
                        )}
                      </Listbox.Option>
                    ))}
                  </Listbox.Options>
                </div>
              );
            }}
          </Listbox>
        </div>
      </>
    );
  };

  const renderAdditionalReason = () => {
    return (
      <>
        <div className="mb-0.8">
          <span className="font-bold text-14">Provide additional context</span>
          <span className="text-mid-grey">*</span>
        </div>
        <div className="h-[8.6rem] relative">
          <textarea
            className="w-full resize-none h-full text-14 py-1.3 pl-1.6 pr-4.5 rounded-[1rem] border-[0.5px] placeholder:text-mid-grey shadow-primary border-lightest-grey hover:border-magenta hover:shadow-input-active focus:border-magenta focus:shadow-input-active focus-visible:border-magenta focus-visible:shadow-input-active scrollbar !outline-none !ring-transparent"
            placeholder="Input reason (0/250)"
            value={otherReason ?? ''}
            onChange={(e) => setOtherReason(e.target.value)}
            maxLength={250}
          />
        </div>
      </>
    );
  };

  return (
    <DefaultModal>
      <div className="max-w-[48rem] min-h-[32rem] rounded-[1rem] border-light-grey bg-white p-[2rem]">
        <div className="text-14 leading-[2.1rem] flex flex-col gap-y-2.4">
          <section className="flex flex-col">
            <h3 className="text-20 font-bold leading-[3rem]">
              Reject appointment request
            </h3>
            <div className="mt-1.6">
              <div>Are you sure you want to reject this appointment?</div>
              <div className="mt-1.6">
                Please let us know the reason so we can improve the booking
                experience.
              </div>
            </div>
            <div className="mt-1.6">{renderCancelReasonDropdown()}</div>
            {selectedCancelReason.id === 'other' && (
              <div className="mt-1.6">{renderAdditionalReason()}</div>
            )}
          </section>
          <section className="flex gap-x-1.6 justify-end">
            <CommonButton variant="secondary" onClick={onClose}>
              Cancel
            </CommonButton>
            <CommonButton
              disabled={
                selectedCancelReason.id === null ||
                (selectedCancelReason.id === 'other' &&
                  otherReason.length === 0)
              }
              onClick={handleSubmit(onSubmit)}
              isLoading={isSubmitting}
            >
              Yes, reject
            </CommonButton>
          </section>
        </div>
      </div>
    </DefaultModal>
  );
};

export default RejectAppointmentModal;
